import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import api from './api';
import VerificationCodeDialog from './VerificationCodeDialog';
import { useUser } from './UserContext';
import BusyOverlay from './BusyOverlay';
import GoogleSignInButton from './GoogleSignInButton';
import AppleSignInButton from './AppleSignInButton';
import { INVITE_CODE_KEY } from './JoinClub';

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [searchParams] = useSearchParams();
  const recaptchaRef = useRef(null);
  const [recaptchaValue, setRecaptchaValue] = useState('');
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLinkLogin = useCallback(async (code) => {
    setError('');
    setIsLoading(true);

    try {
      const res = await api.get(`/link?code=${encodeURIComponent(code)}`);
      setIsLoading(false);

      if (res.status === 200) {
        // Successful login - store user data and navigate
        if (res.data) {
          const userData = res.data.result;
          const user = { ...userData, authentication: userData.token ?? userData.authentication };
          localStorage.setItem('user', JSON.stringify(user));
          setUser(user);
        }
        const pendingInvite = sessionStorage.getItem(INVITE_CODE_KEY);
        navigate(pendingInvite ? `/join?inviteCode=${pendingInvite}` : '/dashboard');
      } else {
        setError('Invalid or expired login link');
      }
    } catch (err) {
      setIsLoading(false);
      setError(err.response?.data || 'Invalid or expired login link');
      console.error('Link login error:', err.response?.data || err.message);
    }
  },[navigate, setUser]);

    // Check for magic link code or OAuth token on component mount
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) handleLinkLogin(code);
  }, [searchParams, handleLinkLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const data = {
      userEmail: form.email,
      userPassword: form.password,
      captchaToken: recaptchaValue,
    };

    try {
      setIsLoading(true);
      const res = await api.post('/login', data);
      setIsLoading(false);
      console.log('Success:', res.data);

      if (res.status === 206) {
        setShowCodeModal(true);
        return;
      } else {
        setError(res.statusText || 'Unexpected Response');
      }
    } catch (err) {
      setIsLoading(false);
      if (err.response?.status === 429) {
        setError('Too many login attempts. Please wait 15 minutes and try again.');
      } else {
        setError(err.response?.data || 'Login failed');
      }
      console.error('Login error:', err.response?.data || err.message);
      setRecaptchaValue('');
      recaptchaRef.current?.reset();
    }
  };

  const handleVerifySuccess = () => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    setShowCodeModal(false);
    const pendingInvite = sessionStorage.getItem(INVITE_CODE_KEY);
    navigate(pendingInvite ? `/join?inviteCode=${pendingInvite}` : '/dashboard');
  };

  const handleRateLimited = () => {
    setShowCodeModal(false);
    setEmailCode('');
    setError('Too many incorrect attempts. Please log in again.');
  };

  const isFormValid = form.email && form.password && recaptchaValue;

  // If we have a magic link code in the URL, show a processing view
  const code = searchParams.get('code');
  if (code) {
    return (
      <>
        <BusyOverlay open={isLoading} message="Processing login link..." />
        <Box sx={{
          marginBottom: 8,
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            opacity: 0.1,
            pointerEvents: 'none',
            zIndex: 0
          }}>
            <img src="/mascot.png" alt="" style={{ width: '100%' }} />
          </div>
          <Paper
            elevation={6}
            sx={{
              padding: 4,
              maxWidth: 500,
              width: '90%',
              textAlign: 'center',
              backgroundColor: theme.palette.background,
              color: theme.palette.text.primary
            }}
          >
            {error ? (
              <>
                <Typography variant="h5" sx={{ marginBottom: 3, fontWeight: 'bold' }}>
                  Login Link Error
                </Typography>
                <Typography variant="body1" color="error" sx={{ marginBottom: 3 }}>
                  {error}
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: 3 }}>
                  The login link may have expired or is invalid. You can request a new one or login normally.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Link
                    to="/myclubisonsnapperish"
                    style={{
                      textDecoration: 'none',
                      color: theme.palette.primary.main,
                    }}
                  >
                    Get New Link
                  </Link>
                  <Link
                    to="/login"
                    style={{
                      textDecoration: 'none',
                      color: theme.palette.primary.main,
                    }}
                  >
                    Normal Login
                  </Link>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h5" sx={{ marginBottom: 3, fontWeight: 'bold' }}>
                  Processing Login Link...
                </Typography>
                <Typography variant="body1">
                  Please wait while we log you in.
                </Typography>
              </>
            )}
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <BusyOverlay open={isLoading} message="Logging you in..." />
      <Box sx={{
        marginBottom: 8,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          opacity: 0.1,
          pointerEvents: 'none',
          zIndex: 0
        }}>
          <img src="/mascot.png" alt="" style={{ width: '100%' }} />
        </div>
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '90%',
            textAlign: 'center',
            backgroundColor: theme.palette.background,
            color: theme.palette.text.primary
          }}
        >
          <Box sx={{ marginBottom: 3 }}>
            <Typography sx={{ fontWeight: 'bold', marginRight: 2 }}>LOGIN</Typography>
            <Link
              to="/signup"
              style={{
                textDecoration: 'none',
                marginRight: theme.spacing(2),
                color: theme.palette.text.primary,
              }}
            >
              SIGN UP
            </Link>
          </Box>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <GoogleSignInButton />
          </Box>
          <Box sx={{ mt: 1 }}>
            <AppleSignInButton />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>or</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
            }}
          >
            <TextField
              required
              label="Email"
              type="email"
              autoComplete="email"
              fullWidth
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              inputProps={{ maxLength: 254 }}
            />
            <TextField
              required
              label="Password"
              type="password"
              autoComplete="current-password"
              fullWidth
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              inputProps={{ maxLength: 1000 }}
            />
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LefeGYrAAAAALRTOHKFlSdFzx9cSnpE6Nt52gcC"
                onChange={(value) => setRecaptchaValue(value)}
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormValid}
              style={{ color: theme.palette.primary.contrastText }}
            >
              LOGIN
            </Button>
          </form>

          <Box sx={{ marginTop: 2 }}>
            <Link
              to="/myclubisonsnapperish"
              style={{
                textDecoration: 'underline',
                color: theme.palette.text.primary,
                fontSize: '0.875rem'
              }}
            >
              My Club is on Snapperish
            </Link>
            <div />
            <Link
              to="/myclubisonsnapperish"
              style={{
                textDecoration: 'none',
                color: theme.palette.text.primary,
                fontSize: '0.875rem'
              }}
            >
              Forgot your password? <span style={{textDecoration: 'underline'}}>Use the My Club login!</span>
            </Link>
          </Box>
        </Paper>
        <VerificationCodeDialog
          open={showCodeModal}
          onClose={() => setShowCodeModal(false)}
          onVerifySuccess={handleVerifySuccess}
          onRateLimited={handleRateLimited}
          email={form.email}
          code={emailCode}
          setCode={setEmailCode}
        />
      </Box>
    </>
  );
};

export default Login;